import {
  AttachmentPreview,
  BoxV2 as Box,
  ButtonV2 as Button,
  Flex,
  MessageBar,
  MultimediaList,
  MultimediaUploader,
  TextInputV2 as TextInput,
} from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { FormLayout, FormSection, FormTitle } from './components';
import { useIdentityStatusControllers } from './hooks';
import { IdentityStatusFormValues } from './types';
import AppealCategory from '../AppealCategory';
import { AttachmentInfoExtras } from '../../types';
import {
  filterAppealCategoriesByVettingType,
  groupAppealCategoryIds,
} from '../../utils';
import { VettingType } from '../../../CSP-Form/constants';
import {
  selectAppealCategories,
  selectVettingSettings,
} from '../../../CSP-Form/selectors';
import {
  AttachmentFolder,
  Brand,
  attachmentController,
  vettingController,
} from '@/apis/core';
import { generateAndDownloadFile } from '@/utils/download';

interface IdentityStatusFormProps {
  brand?: Brand;
  onCancel?: () => void;
  onSubmit?: () => void;
}

enum ConfirmationType {
  None = 'none',
  AppealRequest = 'appealRequest',
}

const IdentityStatusForm: FunctionComponent<IdentityStatusFormProps> = ({
  brand,
  onCancel,
  onSubmit,
}) => {
  const {
    control,
    formState: { isValid },
    getValues,
    handleSubmit,
  } = useForm<IdentityStatusFormValues>({
    mode: 'onTouched',
    defaultValues: {
      appealCategories: [],
      explanation: '',
      attachmentUuids: [],
    },
  });
  const {
    appealCategories: selectedAppealCategoryIds,
    setAppealCategories: setSelectedAppealCategoryIds,
    explanation,
    setExplanation,
    validateExplanation,
    explanationError,
    setAttachmentUuids,
  } = useIdentityStatusControllers(control);
  const appealCategories = useSelector(selectAppealCategories);
  const vettingSettings = useSelector(selectVettingSettings);
  const [proceeding, setProceeding] = useState(false);
  const [attachments, setAttachments] = useState<AttachmentInfoExtras[]>([]);
  const [attachmentPreviewIndex, setAttachmentPreviewIndex] = useState(-1);
  const [confirmationType, setConfirmationType] = useState(
    ConfirmationType.None
  );
  const {
    enabledIdentityAppealCategoryIds,
    disabledIdentityAppealCategoryIds,
  } = useMemo(
    () => groupAppealCategoryIds(vettingSettings, brand),
    [vettingSettings, brand]
  );
  const identityAppealCategories = useMemo(
    () =>
      filterAppealCategoriesByVettingType(
        appealCategories,
        VettingType.Identity
      ).filter((item) =>
        enabledIdentityAppealCategoryIds.includes(item.appealCategoryId)
      ),
    [appealCategories]
  );

  const handleAttachmentUpload = async (file: File) => {
    setProceeding(true);
    const response = await attachmentController.uploadAttachment({
      folder: AttachmentFolder.VetAppeal,
      file,
    });
    if (response) {
      setAttachments([...attachments, { ...response, file }]);
    }
    setProceeding(false);
  };

  const handleAttachmentDownload = async (index: number) => {
    generateAndDownloadFile(attachments[index].file);
  };

  const handleAttachmentPreview = (index: number) => {
    setAttachmentPreviewIndex(index);
  };

  const handleAttachmentDelete = async (index: number) => {
    setProceeding(true);
    const newAttachments = [...attachments];
    const removedAttachments = newAttachments.splice(index, 1);
    const response = await attachmentController.deleteAttachment(
      removedAttachments[0].uuid
    );
    if (response) {
      setAttachments(newAttachments);
    }
    setProceeding(false);
  };

  const handleValid = () => {
    setConfirmationType(ConfirmationType.AppealRequest);
  };

  const hideConfirmation = () => setConfirmationType(ConfirmationType.None);

  const handleConfirm = async () => {
    hideConfirmation();
    if (brand) {
      setProceeding(true);
      try {
        await vettingController.createAppeal(brand.uid, 'identity', {
          evpUid: 'AEGIS',
          ...getValues(),
        });
        onSubmit?.();
      } catch (e) {
        // ignore errors
      }
      setProceeding(false);
    }
  };

  useEffect(() => {
    setAttachmentUuids(attachments.map((attachment) => attachment.uuid));
  }, [attachments]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <FormLayout>
        <FormTitle
          title="Identity Status Appeal Request"
          description="You can submit an appeal up to 45 days after the submission of a Brand. Appeals are charged $11.00 regardless of the outcome. Do not use this to appeal a Standard Vet."
        />
        <FormSection
          title="Appeal Categories"
          description="You are required to select at least one category, but may select more."
        >
          <Flex
            sx={{
              gridColumn: 'span 2',
              flexDirection: 'column',
              gap: 's',
            }}
            data-testid="appealCategories"
          >
            {identityAppealCategories.map((appealCategory) => (
              <AppealCategory
                disabled={disabledIdentityAppealCategoryIds.includes(
                  appealCategory.appealCategoryId
                )}
                checked={selectedAppealCategoryIds.includes(
                  appealCategory.appealCategoryId
                )}
                title={appealCategory.displayName}
                message={appealCategory.description}
                onValueChange={(checked) => {
                  const id = appealCategory.appealCategoryId;
                  const newSelectedAppealCategoryIds = checked
                    ? [...selectedAppealCategoryIds, id]
                    : selectedAppealCategoryIds.filter((item) => item !== id);
                  setSelectedAppealCategoryIds(newSelectedAppealCategoryIds);
                }}
              />
            ))}
          </Flex>
          <Box sx={{ gridColumn: 'span 2' }}>
            <TextInput
              multiline
              error={!!explanationError?.message}
              label="Explanation"
              note={explanationError?.message}
              maxLength={1024}
              minRows={3}
              maxRows={3}
              value={explanation}
              onChange={setExplanation}
              onBlur={validateExplanation}
              data-testid="explanation"
            />
          </Box>
          <Flex sx={{ gridColumn: 'span 2', gap: 'xxl' }}>
            <MultimediaUploader
              style={{ flex: 1 }}
              onChange={handleAttachmentUpload}
              data-testid="uploader"
            />
            <MultimediaList
              style={{ flex: 2 }}
              editable
              loading={proceeding}
              attachments={attachments}
              onDownload={handleAttachmentDownload}
              onSelect={handleAttachmentPreview}
              onDelete={handleAttachmentDelete}
              data-testid="uploads"
            />
          </Flex>
        </FormSection>
        {confirmationType === ConfirmationType.None ? (
          <Flex sx={{ justifyContent: 'flex-end', gap: 'xl' }}>
            <Button
              size="lg"
              shape="square"
              variant="outline"
              color="secondary"
              disabled={proceeding}
              onClick={onCancel}
              data-testid="cancel"
            >
              Cancel
            </Button>
            <Button
              size="lg"
              shape="square"
              variant="outline"
              color="secondary"
              type="submit"
              disabled={!isValid || proceeding}
              data-testid="submit"
            >
              Submit Appeal
            </Button>
          </Flex>
        ) : confirmationType === ConfirmationType.AppealRequest ? (
          <MessageBar
            showClose={false}
            type="warning"
            title="Appeal Request Confirmation"
            primaryAction={{
              label: 'Confirm Appeal',
              onClick: handleConfirm,
            }}
            secondaryAction={{
              label: 'Cancel',
              onClick: hideConfirmation,
            }}
          >
            This appeal will cost $11.00 regardless of the outcome. Do you still
            want to continue?
          </MessageBar>
        ) : null}
      </FormLayout>
      {attachmentPreviewIndex > -1 &&
        attachments[attachmentPreviewIndex].file && (
          <AttachmentPreview
            file={attachments[attachmentPreviewIndex].file}
            mimeType={attachments[attachmentPreviewIndex].mimeType}
            onClose={() => setAttachmentPreviewIndex(-1)}
          />
        )}
    </form>
  );
};

export default IdentityStatusForm;
